/**
 * Wave Z — modify commands (fillet/chamfer/break/lengthen/stretch/pedit/spline)
 * and 3D transforms (move3d/rotate3d/scale2d/scale1d), curve blending and
 * block explode. (docs/architecture/command-parity-plan.md)
 */

import {
    runInteraction, PT, PN, PK, PLoopK, requireSelection, entityToPolyline, curveEntity, dataArrs,
    rubberBand, kernelCurveOf, nurbsCurveEntity, refreshKernelTessellation, deriveEntities,
} from './lib';
import {
    rebuildCurve, curveFromControlPoints, evaluateCurve, curveTangent,
} from '../../geometry/nurbs/curve';
import { curveDomain } from '../../geometry/nurbs/types';
import { undoableAdd, undoableModify, undoableReplace } from '../entityOps';
import { useAppStore } from '../../../state/store';
import { useBlockStore } from '../../../state/commandAuxStores';
import { useSystemVarsStore } from '../../../state/useSystemVarsStore';
import {
    filletCorners, chamferCorners, arcPoints, closestOnSegment, polylineLength,
    resamplePolyline, lineIntersection2, segmentIntersection2,
} from '../geometryMath';
import type { Pt3, Segment2 } from '../geometryMath';
import type { RunnerMap } from '../runnerRegistry';
import type { Entity } from '../../../state/store';
import { notify } from '../../../utils/notify';

function polylineOf(e: Entity | undefined): Pt3[] {
    if (!e) return [];
    return entityToPolyline(e) ?? [];
}
function lineSegments(e: Entity): Segment2[] {
    const poly = polylineOf(e);
    const out: Segment2[] = [];
    for (let i = 0; i < poly.length - 1; i++) out.push({ a: poly[i], b: poly[i + 1] });
    return out;
}

// ---------- data transforms (operate on the raw data bag) ----------
function translateData3(data: Record<string, any>, d: [number, number, number]): Record<string, any> {
    const shift = (p: Pt3): Pt3 => [p[0] + d[0], p[1] + d[1], p[2] + d[2]];
    ['start', 'end', 'position', 'center'].forEach((k) => { if (Array.isArray(data[k])) data[k] = shift(data[k] as Pt3); });
    ['vertices', 'controlPoints'].forEach((k) => { if (Array.isArray(data[k])) data[k] = (data[k] as Pt3[]).map(shift); });
    return data;
}
function rotateAroundAxis(data: Record<string, any>, a: Pt3, b: Pt3, ang: number): Record<string, any> {
    const dir = [b[0] - a[0], b[1] - a[1], b[2] - a[2]];
    const l = Math.hypot(dir[0], dir[1], dir[2]) || 1;
    const [ux, uy, uz] = dir.map((v) => v / l);
    const c = Math.cos(ang), s = Math.sin(ang), t = 1 - c;
    const rot = (p: Pt3): Pt3 => {
        const x = p[0] - a[0], y = p[1] - a[1], z = p[2] - a[2];
        return [
            a[0] + (t * ux * ux + c) * x + (t * ux * uy - s * uz) * y + (t * ux * uz + s * uy) * z,
            a[1] + (t * ux * uy + s * uz) * x + (t * uy * uy + c) * y + (t * uy * uz - s * ux) * z,
            a[2] + (t * ux * uz - s * uy) * x + (t * uy * uz + s * ux) * y + (t * uz * uz + c) * z,
        ];
    };
    ['start', 'end', 'position', 'center'].forEach((k) => { if (Array.isArray(data[k])) data[k] = rot(data[k] as Pt3); });
    ['vertices', 'controlPoints'].forEach((k) => { if (Array.isArray(data[k])) data[k] = (data[k] as Pt3[]).map(rot); });
    return data;
}
function scaleXY(data: Record<string, any>, base: [number, number], f: number): Record<string, any> {
    const sc = (p: Pt3): Pt3 => [base[0] + (p[0] - base[0]) * f, base[1] + (p[1] - base[1]) * f, p[2]];
    ['start', 'end', 'position', 'center'].forEach((k) => { if (Array.isArray(data[k])) data[k] = sc(data[k] as Pt3); });
    ['vertices', 'controlPoints'].forEach((k) => { if (Array.isArray(data[k])) data[k] = (data[k] as Pt3[]).map(sc); });
    return data;
}
function stretchAlong(data: Record<string, any>, base: [number, number], dir: [number, number], f: number): Record<string, any> {
    const along = (p: Pt3) => (p[0] - base[0]) * dir[0] + (p[1] - base[1]) * dir[1];
    const st = (p: Pt3): Pt3 => {
        const a = along(p);
        const perp0 = p[0] - base[0] - a * dir[0], perp1 = p[1] - base[1] - a * dir[1];
        return [base[0] + a * f * dir[0] + perp0, base[1] + a * f * dir[1] + perp1, p[2]];
    };
    ['start', 'end', 'position', 'center'].forEach((k) => { if (Array.isArray(data[k])) data[k] = st(data[k] as Pt3); });
    ['vertices', 'controlPoints'].forEach((k) => { if (Array.isArray(data[k])) data[k] = (data[k] as Pt3[]).map(st); });
    return data;
}

function nearestIndex(poly: Pt3[], p: Pt3): number {
    let best = 0, bd = Infinity;
    poly.forEach((q, i) => {
        const d = Math.hypot(q[0] - p[0], q[1] - p[1], q[2] - p[2]);
        if (d < bd) { bd = d; best = i; }
    });
    return best;
}
function dedupePoints(pts: Pt3[]): Pt3[] {
    const out: Pt3[] = [];
    pts.forEach((p) => {
        const prev = out[out.length - 1];
        if (!prev || Math.hypot(p[0] - prev[0], p[1] - prev[1], p[2] - prev[2]) > 1e-4) out.push(p);
    });
    return out;
}
const dist2 = (a: { x: number; y: number }, b: { x: number; y: number }) => Math.hypot(a.x - b.x, a.y - b.y);

export const modRunners: RunnerMap = {
    'mod.fillet': (args) => {
        const fromArgs = Number(args?.[0]);
        const r = Number.isFinite(fromArgs) && fromArgs > 0 ? fromArgs : 0.2;
        runInteraction({
            steps: [
                PK('FILLET: pick the first line or polyline'),
                PK('FILLET: pick the second line or polyline'),
            ],
            complete: (_p, _v, picks) => {
                const { entities } = useAppStore.getState();
                const a = entities[picks[0]], b = entities[picks[1]];
                if (!a || !b) return 'FILLET: invalid picks.';
                const sa = lineSegments(a)[0], sb = lineSegments(b)[0];
                if (!sa || !sb) return 'FILLET: needs straight segments.';
                const f = filletCorners(sa, sb, r);
                if (!f) return 'FILLET: geometry impossible (parallel or too short).';
                const arc = arcPoints(f.center[0], f.center[1], r, f.startAngle, f.endAngle, f.center[2]);
                // Full fillet: trim the two source edges back to the tangent
                // points (classic 2D joins the loop; here each picked
                // LINE keeps one end at its far side and the other moves to
                // the tangent point), then add the connecting arc.
                undoableModify([picks[0], picks[1]], (data, e) => {
                    const d = { ...data };
                    if (e.id === picks[0]) {
                        const keepFar = Math.hypot(sa.a[0] - f.p1[0], sa.a[1] - f.p1[1]) > Math.hypot(sa.b[0] - f.p1[0], sa.b[1] - f.p1[1]);
                        if (keepFar) d.end = f.p1; else d.start = f.p1;
                    } else {
                        const keepFar = Math.hypot(sb.a[0] - f.p2[0], sb.a[1] - f.p2[1]) > Math.hypot(sb.b[0] - f.p2[0], sb.b[1] - f.p2[1]);
                        if (keepFar) d.end = f.p2; else d.start = f.p2;
                    }
                    return d;
                });
                deriveEntities('mod.fillet', picks, (sources) => {
                    const s1 = lineSegments(sources[0])[0], s2 = lineSegments(sources[1])[0];
                    if (!s1 || !s2) return null;
                    const f2 = filletCorners(s1, s2, r);
                    if (!f2) return null;
                    return [curveEntity(arcPoints(f2.center[0], f2.center[1], r, f2.startAngle, f2.endAngle, f2.center[2]), `Fillet-${Date.now()}`)];
                });
                return `Fillet radius ${r}: edges trimmed and joined by the arc.`;
            },
        });
        return `FILLET: radius ${r} — pick two lines`;
    },

    'mod.chamfer': (args) => {
        const d1 = Number(args?.[0]) || 0.2;
        const d2 = Number(args?.[1]) || d1;
        runInteraction({
            steps: [PK('CHAMFER: pick the first line'), PK('CHAMFER: pick the second line')],
            complete: (_p, _v, picks) => {
                const { entities } = useAppStore.getState();
                const a = entities[picks[0]], b = entities[picks[1]];
                if (!a || !b) return 'CHAMFER: invalid picks.';
                const sa = lineSegments(a)[0], sb = lineSegments(b)[0];
                if (!sa || !sb) return 'CHAMFER: needs straight segments.';
                const c = chamferCorners(sa, sb, d1, d2);
                if (!c) return 'CHAMFER: geometry impossible.';
                // Trim both edges to the chamfer foot points, then connect.
                undoableModify([picks[0], picks[1]], (data, e) => {
                    const d = { ...data };
                    if (e.id === picks[0]) {
                        const keepFar = Math.hypot(sa.a[0] - c.p1[0], sa.a[1] - c.p1[1]) > Math.hypot(sa.b[0] - c.p1[0], sa.b[1] - c.p1[1]);
                        if (keepFar) d.end = c.p1; else d.start = c.p1;
                    } else {
                        const keepFar = Math.hypot(sb.a[0] - c.p2[0], sb.a[1] - c.p2[1]) > Math.hypot(sb.b[0] - c.p2[0], sb.b[1] - c.p2[1]);
                        if (keepFar) d.end = c.p2; else d.start = c.p2;
                    }
                    return d;
                });
                deriveEntities('mod.chamfer', picks, (sources) => {
                    const s1 = lineSegments(sources[0])[0], s2 = lineSegments(sources[1])[0];
                    if (!s1 || !s2) return null;
                    const c2 = chamferCorners(s1, s2, d1, d2);
                    return c2 ? [curveEntity([c2.p1, c2.p2], `Chamfer-${Date.now()}`)] : null;
                });
                return `Chamfer ${d1}x${d2}: edges trimmed and cut.`;
            },
        });
        return `CHAMFER: distances ${d1}x${d2} — pick two lines`;
    },

    'mod.break': () => {
        runInteraction({
            steps: [
                PK('BREAK: pick the object'),
                PT('BREAK: first break point'),
                PT('BREAK: second break point'),
            ],
            complete: (pts, _v, picks) => {
                const { entities } = useAppStore.getState();
                const e = entities[picks[0]];
                if (!e) return 'BREAK: invalid pick.';
                const poly = entityToPolyline(e);
                if (!poly || poly.length < 2) return 'BREAK: unsupported object type.';
                const a = [pts[0].x, pts[0].y, pts[0].z] as Pt3;
                const b = [pts[1].x, pts[1].y, pts[1].z] as Pt3;
                // Project each break point onto the polyline (true point on
                // the curve, not the nearest vertex): walk segments and keep
                // the foot of the perpendicular with the smallest distance.
                const projectOnPoly = (p: Pt3): { idx: number; point: Pt3 } | null => {
                    let best: { idx: number; point: Pt3; d: number } | null = null;
                    for (let i = 0; i < poly.length - 1; i++) {
                        const c = closestOnSegment(p, { a: poly[i], b: poly[i + 1] });
                        if (!best || c.dist < best.d) best = { idx: i, point: c.point, d: c.dist };
                    }
                    return best ? { idx: best.idx, point: best.point } : null;
                };
                const pa = projectOnPoly(a), pb = projectOnPoly(b);
                if (!pa || !pb) return 'BREAK: could not project the break points.';
                // Ordered pair along the polyline (segment index, then t).
                const first = pa.idx < pb.idx ? pa : pb;
                const second = pa.idx < pb.idx ? pb : pa;
                const parts: Pt3[][] = [];
                const head: Pt3[] = poly.slice(0, first.idx + 1);
                head.push(first.point);
                if (head.length >= 2) parts.push(head);
                // 2D removes the portion BETWEEN the break points.
                const tail: Pt3[] = [second.point, ...poly.slice(second.idx + 1)];
                if (tail.length >= 2) parts.push(tail);
                if (parts.length < 2) return 'BREAK: cut too near the ends.';
                undoableReplace([picks[0]], parts.map((pp, i) =>
                    curveEntity(pp, `${e.name} ${String.fromCharCode(65 + i)}`)));
                return `Break complete: ${parts.length} segment(s) remain.`;
            },
        });
        return 'BREAK: pick object';
    },

    'mod.lengthen': (args) => {
        const delta = Number(args?.[0]) || 0.5;
        runInteraction({
            steps: [PK('LENGTHEN: pick the line to lengthen')],
            complete: (_p, _v, picks) => {
                const { entities } = useAppStore.getState();
                const e = entities[picks[0]];
                if (!e || e.type !== 'line') return 'LENGTHEN: pick a line.';
                undoableModify(picks, (data) => {
                    const s = data.start as Pt3, t = data.end as Pt3;
                    const dir = [t[0] - s[0], t[1] - s[1], t[2] - s[2]];
                    const l = Math.hypot(dir[0], dir[1], dir[2]) || 1;
                    return { ...data, end: [t[0] + (dir[0] / l) * delta, t[1] + (dir[1] / l) * delta, t[2] + (dir[2] / l) * delta] };
                });
                return `Lengthened by ${delta}.`;
            },
        });
        return `LENGTHEN: delta ${delta} — pick a line`;
    },

    'mod.stretch': () => {
        runInteraction({
            steps: [
                PT('STRETCH: first corner of crossing window'),
                PT('STRETCH: opposite corner of crossing window'),
                PT('STRETCH: base point'),
                PT('STRETCH: second point (displacement)'),
            ],
            preview: rubberBand('#00ff00'),
            complete: (pts) => {
                const { entities } = useAppStore.getState();
                const [c1, c2] = [pts[0], pts[1]];
                const minX = Math.min(c1.x, c2.x), maxX = Math.max(c1.x, c2.x);
                const minY = Math.min(c1.y, c2.y), maxY = Math.max(c1.y, c2.y);
                const d: [number, number, number] = [
                    pts[3].x - pts[2].x, pts[3].y - pts[2].y, pts[3].z - pts[2].z,
                ];
                // Crossing-window selection: an object participates when any
                // of its points lies inside the rectangle. Only the points
                // INSIDE the window move (elastic stretch); the rest stay.
                const inside = (p: number[]) => p[0] >= minX && p[0] <= maxX && p[1] >= minY && p[1] <= maxY;
                const stretchedIds: string[] = [];
                Object.values(entities).forEach((e) => {
                    if (!e.visible || e.locked) return;
                    const poly = entityToPolyline(e);
                    if (!poly || poly.length === 0) return;
                    if (!poly.some(inside)) return;
                    stretchedIds.push(e.id);
                });
                if (stretchedIds.length === 0) return 'STRETCH: crossing window selected no objects.';
                // Line/rect entities move their in-window endpoints; all
                // point-bag entities move their in-window vertices.
                undoableModify(stretchedIds, (data) => {
                    const next = { ...data };
                    const moveIf = (p: number[]): number[] => (inside(p) ? [p[0] + d[0], p[1] + d[1], (p[2] ?? 0) + d[2]] : p);
                    if (Array.isArray(next.start)) next.start = moveIf(next.start as number[]);
                    if (Array.isArray(next.end)) next.end = moveIf(next.end as number[]);
                    if (Array.isArray(next.center)) next.center = moveIf(next.center as number[]);
                    ['vertices', 'controlPoints'].forEach((k) => {
                        if (Array.isArray(next[k])) next[k] = (next[k] as number[][]).map(moveIf);
                    });
                    return next;
                });
                return `STRETCH: stretched ${stretchedIds.length} object(s) through the crossing window.`;
            },
        });
        return 'STRETCH: specify crossing window corners';
    },

    'mod.pedit': (args) => {
        const mode = (args?.[0] ?? 'join').toLowerCase();
        runInteraction({
            steps: mode === 'width'
                ? [PLoopK('PEDIT: pick polylines (Enter to finish)', 1), PN('PEDIT: new width <0>')]
                : [PLoopK('PEDIT: pick polylines (Enter to finish)', 2)],
            complete: (_p, values, picks) => {
                if (mode === 'width') {
                    // PEDIT Width: set the global polyline width — the
                    // renderer draws parallel offset edges at this size.
                    const w = Math.max(0, Number(values[1]) || 0);
                    undoableModify(picks, (d) => ({ ...d, width: w }));
                    return `PEDIT: polyline width set to ${w}.`;
                }
                const { entities } = useAppStore.getState();
                const polys = picks.map((id) => entities[id]).filter(Boolean) as Entity[];
                if (polys.length < 2) return 'PEDIT: pick 2+ polylines to join.';
                const merged = polys.flatMap(polylineOf);
                undoableReplace(picks, [curveEntity(dedupePoints(merged), `Pedit-${Date.now()}`, { closed: !!polys[0].data.closed })]);
                return `PEDIT: joined ${polys.length} polylines into one.`;
            },
        });
        return 'PEDIT: pick polylines';
    },

    'mod.splinedit': (args) => {
        const mode = args?.[0]?.toLowerCase();
        runInteraction({
            steps: [PK('SPLINEDIT: pick a spline')],
            complete: (_p, _v, picks) => {
                const { entities } = useAppStore.getState();
                const e = entities[picks[0]];
                if (!e) return 'SPLINEDIT: invalid pick.';
                const c = kernelCurveOf(e);
                if (!c) return 'SPLINEDIT: not a curve.';
                if (mode === 'close') {
                    undoableReplace([picks[0]], [nurbsCurveEntity({ ...c, closed: true }, `Spline-${Date.now()}`)]);
                    return 'Spline closed.';
                }
                // refine = add CVs around the existing shape (degree-preserving)
                const refined = rebuildCurve(c, c.degree, Math.min(c.points.length * 2, 48));
                undoableReplace([picks[0]], [nurbsCurveEntity(refined, `Spline-${Date.now()}`)]);
                return `Spline refined to ${refined.points.length} control points.`;
            },
        });
        return 'SPLINEDIT: pick a spline';
    },

    'mod.move3d': () => {
        runInteraction({
            steps: [PT('3DMOVE: base point'), PT('3DMOVE: target point')],
            complete: (pts) => {
                const ids = useAppStore.getState().selectedIds;
                if (ids.length === 0) return '3DMOVE: select objects first.';
                const d: [number, number, number] = [pts[1].x - pts[0].x, pts[1].y - pts[0].y, pts[1].z - pts[0].z];
                undoableModify(ids, (data) => translateData3(data, d));
                return `3D move applied to ${ids.length} object(s).`;
            },
        });
        return '3DMOVE: base point';
    },

    'mod.rotate3d': (args) => {
        const angleDeg = Number(args?.[0]) || 45;
        runInteraction({
            steps: [PT('3DROTATE: axis start'), PT('3DROTATE: axis end')],
            complete: (pts) => {
                const ids = useAppStore.getState().selectedIds;
                if (ids.length === 0) return '3DROTATE: select objects first.';
                const a: Pt3 = [pts[0].x, pts[0].y, pts[0].z];
                const b: Pt3 = [pts[1].x, pts[1].y, pts[1].z];
                undoableModify(ids, (data) => rotateAroundAxis(data, a, b, (angleDeg * Math.PI) / 180));
                return `3D rotate ${angleDeg}° applied.`;
            },
        });
        return `3DROTATE: angle ${angleDeg}° — define axis`;
    },

    'mod.scale2d': () => {
        runInteraction({
            steps: [PT('SCALE2D: base point'), PT('SCALE2D: reference point'), PT('SCALE2D: target point')],
            complete: (pts) => {
                const ids = useAppStore.getState().selectedIds;
                if (ids.length === 0) return 'SCALE2D: select objects first.';
                const base = pts[0];
                const f = dist2(pts[1], base) > 1e-6 ? dist2(pts[2], base) / dist2(pts[1], base) : 1;
                undoableModify(ids, (data) => scaleXY(data, [base.x, base.y], f));
                return `Scale 2D factor ${f.toFixed(3)} applied.`;
            },
        });
        return 'SCALE2D: base point';
    },

    'mod.scale1d': () => {
        runInteraction({
            steps: [PT('SCALE1D: base point'), PT('SCALE1D: direction point'), PT('SCALE1D: target point')],
            complete: (pts) => {
                const ids = useAppStore.getState().selectedIds;
                if (ids.length === 0) return 'SCALE1D: select objects first.';
                const a = pts[0], d = pts[1], t = pts[2];
                const l = Math.hypot(d.x - a.x, d.y - a.y) || 1;
                const dir: [number, number] = [(d.x - a.x) / l, (d.y - a.y) / l];
                const along = (p: { x: number; y: number }) => (p.x - a.x) * dir[0] + (p.y - a.y) * dir[1];
                const denom = along(d);
                if (Math.abs(denom) < 1e-9) return 'SCALE1D: degenerate direction.';
                const f = along(t) / denom;
                undoableModify(ids, (data) => stretchAlong(data, [a.x, a.y], dir, f));
                return `Scale 1D factor ${f.toFixed(3)} applied.`;
            },
        });
        return 'SCALE1D: base point';
    },

    'mod.explodeBlock': () => {
        runInteraction({
            steps: [PK('EXPLODEBLOCK: pick a block reference', ['insert', 'block'])],
            complete: (_p, _v, picks) => {
                const { entities } = useAppStore.getState();
                const e = entities[picks[0]];
                if (!e || e.type !== 'insert') return 'EXPLODEBLOCK: not a block reference.';
                const def = useBlockStore.getState().defs[String((e.data as Record<string, unknown>).blockName ?? '')];
                if (!def) return 'EXPLODEBLOCK: block definition missing.';
                const p = (e.data.position as number[]) ?? [0, 0, 0];
                const scale = typeof e.data.scale === 'number' && e.data.scale > 0 ? e.data.scale : 1;
                const rot = ((e.data.rotation as number) ?? 0) * Math.PI / 180;
                const cos = Math.cos(rot), sin = Math.sin(rot);
                // Local coords are defined relative to the block base point
                // (centroid, stored at [0,0,0]): rotate+scale each entity's
                // points around the base, then translate to the insertion.
                const mapPt = (pt: number[]): number[] => {
                    const x = (pt[0] ?? 0) * scale, y = (pt[1] ?? 0) * scale;
                    return [(p[0] ?? 0) + x * cos - y * sin, (p[1] ?? 0) + x * sin + y * cos, (p[2] ?? 0) + (pt[2] ?? 0) * scale];
                };
                const placed = def.entities.map((src) => {
                    const data = structuredClone(src.data) as Record<string, any>;
                    ['start', 'end', 'position', 'center'].forEach((k) => { if (Array.isArray(data[k])) data[k] = mapPt(data[k]); });
                    ['vertices', 'controlPoints'].forEach((k) => { if (Array.isArray(data[k])) data[k] = data[k].map(mapPt); });
                    if (typeof data.rotation === 'number') data.rotation += rot;
                    if (typeof data.startAngle === 'number' && typeof data.endAngle === 'number') {
                        data.startAngle += rot; data.endAngle += rot;
                    }
                    if (typeof data.radius === 'number') data.radius *= scale;
                    if (typeof data.radiusA === 'number') { data.radiusA *= scale; data.radiusB = (data.radiusB ?? data.radiusA) * scale; }
                    return { ...src, id: `cad:${crypto.randomUUID()}`, data };
                });
                undoableReplace([picks[0]], placed);
                return `Block "${def.name}" exploded into ${def.entities.length} component(s).`;
            },
        });
        return 'EXPLODEBLOCK: pick a block';
    },

    'mod.rebuild': (args) => {
        const degree = Math.max(1, Number(args?.[0]) || 3);
        runInteraction({
            steps: [PK('REBUILD: pick a curve')],
            complete: (_p, _v, picks) => {
                const { entities } = useAppStore.getState();
                const e = entities[picks[0]];
                if (!e) return 'REBUILD: invalid pick.';
                const c = kernelCurveOf(e);
                if (!c) return 'REBUILD: not a curve.';
                // Real rebuild: fresh point count and degree from resampled
                // curve geometry (least-squares collocation in the kernel).
                const rebuilt = rebuildCurve(c, degree, Math.max(degree + 1, c.points.length));
                undoableReplace([picks[0]], [nurbsCurveEntity(rebuilt, `Rebuilt-${Date.now()}`)]);
                return `Curve rebuilt to degree ${rebuilt.degree} with ${rebuilt.points.length} control points.`;
            },
        });
        return `REBUILD: degree ${degree} — pick a curve`;
    },

    'mod.blendCrv': () => {
        runInteraction({
            steps: [PK('BLENDCRV: pick the first curve'), PK('BLENDCRV: pick the second curve')],
            complete: (_p, _v, picks) => {
                const created = deriveEntities('mod.blendCrv', picks, (sources) => {
                    const ca = kernelCurveOf(sources[0]);
                    const cb = kernelCurveOf(sources[1]);
                    if (!ca || !cb) return null;
                    // Hermite blend: end points + true kernel end tangents give
                    // a G1-continuous connector (tangent-matched at both ends).
                    const [ta, tb] = [curveDomain(ca), curveDomain(cb)];
                    const pa = evaluateCurve(ca, ta[1]);
                    const pb = evaluateCurve(cb, tb[0]);
                    const da = curveTangent(ca, ta[1]);
                    const db = curveTangent(cb, tb[0]);
                    const dist = Math.hypot(pb[0] - pa[0], pb[1] - pa[1], pb[2] - pa[2]) / 3;
                    const net: Pt3[] = [
                        pa,
                        [pa[0] + da[0] * dist, pa[1] + da[1] * dist, pa[2] + da[2] * dist],
                        [pb[0] - db[0] * dist, pb[1] - db[1] * dist, pb[2] - db[2] * dist],
                        pb,
                    ];
                    return [nurbsCurveEntity(curveFromControlPoints(net, 3), `Blend-${Date.now()}`)];
                });
                if (!created) return 'BLENDCRV: both picks must be curves.';
                return 'Tangent-blended curve created between the two ends (G1).';
            },
        });
        return 'BLENDCRV: pick two curves';
    },

    'mod.matchCrv': () => {
        runInteraction({
            steps: [PK('MATCHCRV: pick the curve to adjust'), PK('MATCHCRV: pick the target curve')],
            complete: (_p, _v, picks) => {
                const { entities } = useAppStore.getState();
                const src = kernelCurveOf(entities[picks[0]]);
                const tgt = kernelCurveOf(entities[picks[1]]);
                if (!src || !tgt) return 'MATCHCRV: both picks must be curves.';
                // G1 match: pull the source end to the target start and align
                // the last control segment with the target's start tangent.
                const [sd, td] = [curveDomain(src), curveDomain(tgt)];
                void sd;
                const ps = evaluateCurve(tgt, td[0]);
                const tan = curveTangent(tgt, td[0]);
                const cv = src.points.map((p) => [...p] as Pt3);
                const last = cv.length - 1;
                const prev = cv[last - 1];
                const segLen = Math.hypot(prev[0] - cv[last][0], prev[1] - cv[last][1], prev[2] - cv[last][2]) || 0.5;
                cv[last] = ps;
                if (last >= 1) {
                    cv[last - 1] = [ps[0] - tan[0] * segLen, ps[1] - tan[1] * segLen, ps[2] - tan[2] * segLen] as Pt3;
                }
                const matched = { ...src, points: cv };
                const e = entities[picks[0]];
                const data = refreshKernelTessellation({ ...e.data, curve: matched });
                undoableModify([picks[0]], () => data);
                return 'MatchCrv: source end rebuilt for G1 tangency with the target.';
            },
        });
        return 'MATCHCRV: pick source then target';
    },

    // ---------------- TRIM / EXTEND on CAD entities ----------------
    // Command-line native drafting: pick a boundary edge, pick the object to
    // modify, then click the side to remove (trim) or the side to grow
    // (extend). Operates on the first straight segment of each pick, which is
    // what LINE/POLYLINE drafts produce — the honest subset the engine can
    // solve analytically. Replaces the old silent 'tool activated' dead end
    // that only worked through the BIM class tool.
    // EDGEMODE parity: 1 = boundary edges extend infinitely (classic
    // default here); 0 = only true segment crossings count.
    'mod.trim': () => {
        runInteraction({
            steps: [
                PK('TRIM: pick the cutting edge'),
                PK('TRIM: pick the object to trim'),
                PT('TRIM: click the end to remove'),
            ],
            complete: (pts, _v, picks) => {
                const { entities } = useAppStore.getState();
                const bound = lineSegments(entities[picks[0]])[0];
                const target = entities[picks[1]];
                const tSegs = lineSegments(target);
                if (!bound || tSegs.length === 0) return 'TRIM: needs straight segments.';
                if (!Array.isArray(target.data.start) || !Array.isArray(target.data.end)) {
                    return 'TRIM: only line objects are supported by the command line yet.';
                }
                const edgeMode = useSystemVarsStore.getState().EDGEMODE;
                let x: Pt3 | null;
                if (edgeMode === 0) {
                    x = segmentIntersection2(bound, tSegs[0]);
                    if (!x) return 'TRIM: edges do not cross within their extents (EDGEMODE=0).';
                } else {
                    x = lineIntersection2(bound, tSegs[0]);
                    if (!x) return 'TRIM: edge and object are parallel — no intersection.';
                }
                const keep = pts[2];
                const startDist = Math.hypot(tSegs[0].a[0] - keep.x, tSegs[0].a[1] - keep.y);
                const endDist = Math.hypot(tSegs[0].b[0] - keep.x, tSegs[0].b[1] - keep.y);
                undoableModify([picks[1]], (data) => {
                    // Remove the end farther from the clicked keep point.
                    if (startDist > endDist) data.start = x; else data.end = x;
                    return data;
                });
                return 'Trimmed to the cutting edge.';
            },
        });
        return 'TRIM: pick cutting edge, object, then end to remove';
    },

    'mod.extend': () => {
        runInteraction({
            steps: [
                PK('EXTEND: pick the boundary edge'),
                PK('EXTEND: pick the object to extend'),
                PT('EXTEND: click the end to grow toward the boundary'),
            ],
            complete: (pts, _v, picks) => {
                const { entities } = useAppStore.getState();
                const bound = lineSegments(entities[picks[0]])[0];
                const target = entities[picks[1]];
                const tSegs = lineSegments(target);
                if (!bound || tSegs.length === 0) return 'EXTEND: needs straight segments.';
                if (!Array.isArray(target.data.start) || !Array.isArray(target.data.end)) {
                    return 'EXTEND: only line objects are supported by the command line yet.';
                }
                const x = lineIntersection2(bound, tSegs[0]);
                if (!x) return 'EXTEND: boundary and object are parallel — no intersection.';
                const side = pts[2];
                const startDist = Math.hypot(tSegs[0].a[0] - side.x, tSegs[0].a[1] - side.y);
                const endDist = Math.hypot(tSegs[0].b[0] - side.x, tSegs[0].b[1] - side.y);
                // Safety: the extension must grow toward the intersection.
                const growFrom = startDist < endDist ? tSegs[0].a : tSegs[0].b;
                const toX = Math.hypot(x[0] - growFrom[0], x[1] - growFrom[1]);
                const segLen = Math.hypot(tSegs[0].b[0] - tSegs[0].a[0], tSegs[0].b[1] - tSegs[0].a[1]);
                if (toX < segLen) return 'EXTEND: boundary is behind the object end — nothing to grow.';
                undoableModify([picks[1]], (data) => {
                    if (startDist < endDist) data.start = x; else data.end = x;
                    return data;
                });
                return 'Extended to the boundary edge.';
            },
        });
        return 'EXTEND: pick boundary, object, then end to grow';
    },
};
